import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Check, FileText, Image, Loader2, Mic, Paperclip, Send, Square } from "lucide-react";
import { toast } from "sonner";
import api, { getStoredAuthToken } from "@/lib/api";

const ACCEPTED_FILES = ".jpg,.jpeg,.png,.webp,.pdf";

function attachmentUrl(attachment) {
  const token = getStoredAuthToken();
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const authQuery = token ? `?auth=${encodeURIComponent(token)}` : "";
  return `${baseUrl}/api/student-live-chat/attachments/${attachment.id}${authQuery}`;
}

function formatMessageTime(value) {
  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function ChatAttachment({ attachment }) {
  if (!attachment) {
    return null;
  }

  const url = attachmentUrl(attachment);
  const isImage = attachment.content_type?.startsWith("image/");
  const isAudio = attachment.content_type?.startsWith("audio/");
  if (isAudio) {
    return <audio controls className="mt-3 w-full" data-testid={`student-chat-audio-${attachment.id}`}><source src={url} type={attachment.content_type} /></audio>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={`student-chat-attachment-${attachment.id}`}
      className="mt-3 flex max-w-full items-center gap-2 border border-[#B7E4C7] bg-white px-3 py-2 text-sm font-bold text-[#0B6B3A] transition-colors hover:bg-[#E8F6EE]"
    >
      {isImage ? <Image className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
      <span className="truncate">{attachment.original_filename}</span>
    </a>
  );
}

function ChatMessageList({ emptyMessage, loading, messages }) {
  if (loading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center" data-testid="student-chat-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#27AE60]" />
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div
        className="flex min-h-[260px] items-center justify-center text-center text-sm text-[#6B7280]"
        data-testid="student-chat-empty-state"
      >
        {emptyMessage}
      </div>
    );
  }

  return messages.map((item) => {
    const isAiAssistant = item.sender_role === "ai_assistant";
    return (
      <article
        key={item.id}
        data-testid={`student-chat-message-${item.id}`}
        className={[
          "max-w-[88%] border px-4 py-3",
          item.sender_role === "student"
            ? "ml-auto border-[#B7E4C7] bg-[#F0FBF5]"
            : isAiAssistant
              ? "border-[#8ED0A8] bg-[#F9FAFB]"
              : "border-gray-200 bg-white",
        ].join(" ")}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#1F2937]">
            {isAiAssistant && <Bot className="h-4 w-4 text-[#0B6B3A]" aria-hidden="true" />}
            {item.sender_name}
          </p>
          <time className="text-xs text-[#6B7280]">{formatMessageTime(item.created_at)}</time>
        </div>
        {item.message && (
          <p
            className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-[#374151]"
            data-testid={`student-chat-message-content-${item.id}`}
          >
            {item.message}
          </p>
        )}
        <ChatAttachment attachment={item.attachment} />
      </article>
    );
  });
}

function ChatComposer({ disabled, onSend, studentId, useAiAssistant = false }) {
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingLocked, setRecordingLocked] = useState(false);
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);
  const audioPartsRef = useRef([]);
  const recordStartYRef = useRef(0);

  const sendVoiceNote = async (file) => {
    const formData = new FormData();
    formData.append("message", "");
    if (studentId) formData.append("student_id", studentId);
    formData.append("attachment", file);
    setSending(true);
    try {
      const response = await api.post("/student-live-chat/messages", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onSend(response.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || "Voice note belum berhasil dikirim."); }
    finally { setSending(false); }
  };

  const startVoiceRecording = async (event) => {
    if (sending || recording) return;
    try {
      recordStartYRef.current = event.clientY;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioPartsRef.current = [];
      recorder.ondataavailable = (item) => audioPartsRef.current.push(item.data);
      recorder.onstop = () => {
        const blob = new Blob(audioPartsRef.current, { type: recorder.mimeType || "audio/webm" });
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false); setRecordingLocked(false);
        if (blob.size) sendVoiceNote(new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type }));
      };
      recorder.start(); recorderRef.current = recorder; setRecording(true);
    } catch { toast.error("Izin mikrofon diperlukan untuk merekam voice note."); }
  };
  const moveVoiceRecording = (event) => { if (recording && !recordingLocked && recordStartYRef.current - event.clientY > 48) setRecordingLocked(true); };
  const releaseVoiceRecording = () => { if (recording && !recordingLocked) recorderRef.current?.stop(); };
  const stopLockedVoiceRecording = () => { if (recording && recordingLocked) recorderRef.current?.stop(); };

  const sendAiAssistedMessage = async (text) => {
    const token = getStoredAuthToken();
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/student-live-chat/messages/ai-stream`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ message: text }),
      },
    );
    if (!response.ok || !response.body) {
      throw new Error("Asisten MDJ belum dapat menjawab pertanyaan.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      events.forEach((rawEvent) => {
        const eventType = rawEvent.split("\n").find((line) => line.startsWith("event: "));
        const dataLine = rawEvent.split("\n").find((line) => line.startsWith("data: "));
        if (!dataLine) {
          return;
        }
        try {
          const data = JSON.parse(dataLine.slice(6));
          if (["student_message", "ai_message"].includes(eventType?.slice(7)) && data.message) {
            onSend(data.message);
          }
          if (eventType?.slice(7) === "done" && data.escalated) {
            toast.info("Pertanyaan Anda diteruskan sebagai rekomendasi untuk Admin.");
          }
        } catch {
          // Ignore malformed stream events and keep the ongoing conversation usable.
        }
      });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if ((!trimmedMessage && !attachment) || sending || disabled) {
      return;
    }

    const formData = new FormData();
    formData.append("message", trimmedMessage);
    if (studentId) {
      formData.append("student_id", studentId);
    }
    if (attachment) {
      formData.append("attachment", attachment);
    }

    setSending(true);
    try {
      if (useAiAssistant && trimmedMessage && !attachment) {
        await sendAiAssistedMessage(trimmedMessage);
      } else {
        const response = await api.post("/student-live-chat/messages", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onSend(response.data.message);
      }
      setMessage("");
      setAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Pesan belum berhasil dikirim.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border-t border-gray-200 p-4"
      data-testid="student-chat-send-form"
    >
      <label className="sr-only" htmlFor="student-chat-message-input">
        Tulis pesan untuk admin
      </label>
      <textarea
        id="student-chat-message-input"
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="Tulis pesan untuk admin..."
        data-testid="student-chat-message-input"
        className="min-h-[84px] w-full resize-y border border-gray-200 bg-white px-3 py-2.5 text-sm text-[#1F2937] outline-none transition-colors focus:border-[#27AE60] focus:ring-2 focus:ring-[#E8F6EE]"
      />
      <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
        {(recording || attachment) && (
          <span
            className="mr-auto max-w-full truncate text-xs font-semibold text-[#4B5563]"
            data-testid={recording ? "student-chat-recording-status" : "student-chat-selected-file"}
          >
            {recording
              ? recordingLocked
                ? "Rekaman dikunci — tekan mikrofon untuk kirim"
                : "Merekam — lepas untuk kirim"
              : attachment?.name}
          </span>
        )}
        <label
          htmlFor="student-chat-attachment-input"
          title="Lampirkan dokumen"
          aria-label="Lampirkan dokumen"
          data-testid="student-chat-attachment-label"
          className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-[#B7E4C7] text-[#0B6B3A] transition-colors hover:bg-[#E8F6EE]"
        >
          <Paperclip className="h-5 w-5" />
        </label>
        <input
          ref={fileInputRef}
          id="student-chat-attachment-input"
          type="file"
          accept={`${ACCEPTED_FILES},.mp3,.wav,.ogg,.webm,.m4a`}
          onChange={(event) => setAttachment(event.target.files?.[0] || null)}
          data-testid="student-chat-attachment-input"
          className="sr-only"
        />
        <button
          type="button"
          onPointerDown={startVoiceRecording}
          onPointerMove={moveVoiceRecording}
          onPointerUp={releaseVoiceRecording}
          onPointerCancel={releaseVoiceRecording}
          onClick={stopLockedVoiceRecording}
          data-testid="student-chat-record-button"
          className={[
            "inline-flex h-11 w-11 items-center justify-center rounded-full text-white",
            recording ? "bg-[#DC2626]" : "bg-[#0B6B3A]",
          ].join(" ")}
          aria-label={recordingLocked ? "Kirim voice note" : "Tahan untuk rekam voice note"}
        >
          {recordingLocked ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
        </button>
        <button
          type="submit"
          disabled={disabled || (!message.trim() && !attachment) || sending}
          data-testid="student-chat-send-button"
          className="inline-flex min-h-11 items-center justify-center gap-2 bg-[#0B6B3A] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#07532D] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Kirim Pesan
        </button>
      </div>
    </form>
  );
}

export default function StudentLiveChat() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMessages = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await api.get("/student-live-chat/messages");
      setMessages(response.data.messages || []);
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || "Live Chat Admin belum dapat dimuat.");
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadMessages();
    const intervalId = window.setInterval(() => loadMessages({ silent: true }), 15000);
    return () => window.clearInterval(intervalId);
  }, [loadMessages]);

  return (
    <section className="space-y-6" data-testid="student-live-chat-manager">
      <div className="border-l-4 border-[#0B6B3A] bg-[#F0FBF5] px-5 py-5">
        <p className="text-xs font-bold uppercase tracking-wide text-[#0B6B3A]">Bantuan Pendaftaran</p>
        <h2 className="mt-1 font-display text-2xl font-extrabold text-[#1F2937]">Live Chat Admin</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#4B5563]">
          Asisten MDJ menjawab pertanyaan umum. Pertanyaan yang membutuhkan pemeriksaan akun atau
          berkas akan diteruskan kepada Admin.
        </p>
      </div>
      <div className="border border-gray-200 bg-white shadow-sm">
        <div className="max-h-[440px] min-h-[260px] space-y-4 overflow-y-auto p-5 mdj-scrollbar" data-testid="student-chat-message-list">
          <ChatMessageList
            emptyMessage="Belum ada percakapan. Kirim pesan untuk memulai bantuan."
            loading={loading}
            messages={messages}
          />
        </div>
        <ChatComposer
          useAiAssistant
          onSend={(item) => setMessages((previous) => [...previous, item])}
        />
      </div>
    </section>
  );
}

export function AdminStudentLiveChat() {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [loading, setLoading] = useState(true);

  const loadConversations = useCallback(async () => {
    try {
      const response = await api.get("/student-live-chat/conversations");
      const nextConversations = response.data.conversations || [];
      setConversations(nextConversations);
      setSelectedStudentId((current) => current || nextConversations[0]?.student_id || "");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Percakapan mahasiswa belum dapat dimuat.");
    }
  }, []);

  const loadMessages = useCallback(async (studentId) => {
    if (!studentId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get("/student-live-chat/messages", { params: { student_id: studentId } });
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Pesan mahasiswa belum dapat dimuat.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecommendations = useCallback(async (studentId) => {
    if (!studentId) {
      setRecommendations([]);
      return;
    }
    try {
      const response = await api.get("/student-live-chat/ai-recommendations", {
        params: { student_id: studentId },
      });
      setRecommendations(response.data.recommendations || []);
    } catch {
      setRecommendations([]);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    loadMessages(selectedStudentId);
    loadRecommendations(selectedStudentId);
  }, [loadMessages, loadRecommendations, selectedStudentId]);

  const resolveRecommendation = async (recommendationId) => {
    try {
      await api.patch(`/student-live-chat/ai-recommendations/${recommendationId}/resolve`);
      setRecommendations((previous) => previous.map((item) => (
        item.id === recommendationId ? { ...item, status: "resolved" } : item
      )));
      toast.success("Rekomendasi AI ditandai sudah ditindaklanjuti.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rekomendasi belum dapat diperbarui.");
    }
  };

  const selectedConversation = conversations.find((item) => item.student_id === selectedStudentId);
  return (
    <div className="grid min-w-0 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]" data-testid="admin-student-chat-inbox">
      <aside className="min-w-0 border border-gray-200 bg-white" data-testid="admin-student-chat-conversation-list">
        <div className="border-b border-gray-200 px-4 py-3">
          <p className="text-sm font-extrabold text-[#1F2937]">Pesan Mahasiswa</p>
        </div>
        <div className="max-h-[520px] divide-y divide-gray-100 overflow-y-auto mdj-scrollbar">
          {conversations.length === 0 ? (
            <p className="p-4 text-sm text-[#6B7280]" data-testid="admin-student-chat-empty-state">
              Belum ada pesan mahasiswa di wilayah Anda.
            </p>
          ) : conversations.map((conversation) => (
            <button
              key={conversation.student_id}
              type="button"
              onClick={() => setSelectedStudentId(conversation.student_id)}
              data-testid={`admin-student-chat-conversation-${conversation.student_id}`}
              className={[
                "w-full px-4 py-3 text-left transition-colors",
                selectedStudentId === conversation.student_id ? "bg-[#F0FBF5]" : "hover:bg-gray-50",
              ].join(" ")}
            >
              <p className="truncate text-sm font-bold text-[#1F2937]">{conversation.student_name}</p>
              <p className="mt-1 truncate text-xs text-[#6B7280]">{conversation.last_message}</p>
              {conversation.region && <p className="mt-1 text-xs font-bold text-[#0B6B3A]">{conversation.region}</p>}
            </button>
          ))}
        </div>
      </aside>
      <section className="min-w-0 border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-5 py-3">
          <p className="text-sm font-extrabold text-[#1F2937]" data-testid="admin-student-chat-title">
            {selectedConversation ? `Chat dengan ${selectedConversation.student_name}` : "Pilih percakapan mahasiswa"}
          </p>
        </div>
        {recommendations.filter((item) => item.status === "open").length > 0 && (
          <div className="border-b border-[#B7E4C7] bg-[#F0FBF5] p-4" data-testid="admin-ai-recommendations">
            <p className="flex items-center gap-2 text-sm font-extrabold text-[#0B6B3A]">
              <Bot className="h-4 w-4" aria-hidden="true" />
              Rekomendasi AI untuk Admin
            </p>
            <div className="mt-3 space-y-3">
              {recommendations.filter((item) => item.status === "open").map((item) => (
                <article
                  key={item.id}
                  className="border border-[#B7E4C7] bg-white p-3"
                  data-testid={`admin-ai-recommendation-${item.id}`}
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-[#0B6B3A]">
                    Perlu tindak lanjut Admin
                  </p>
                  <p className="mt-1 text-sm text-[#374151]">{item.reason}</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#4B5563]">
                    {item.suggested_response}
                  </p>
                  <button
                    type="button"
                    onClick={() => resolveRecommendation(item.id)}
                    data-testid={`resolve-ai-recommendation-${item.id}`}
                    className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-[#0B6B3A] transition-colors hover:text-[#27AE60]"
                  >
                    <Check className="h-4 w-4" aria-hidden="true" />
                    Tandai Ditindaklanjuti
                  </button>
                </article>
              ))}
            </div>
          </div>
        )}
        <div className="max-h-[356px] min-h-[260px] space-y-4 overflow-y-auto p-5 mdj-scrollbar" data-testid="admin-student-chat-message-list">
          <ChatMessageList
            emptyMessage="Pilih percakapan dari daftar di samping."
            loading={loading}
            messages={messages}
          />
        </div>
        <ChatComposer
          disabled={!selectedStudentId}
          onSend={(item) => {
            setMessages((previous) => [...previous, item]);
            loadConversations();
          }}
          studentId={selectedStudentId}
        />
      </section>
    </div>
  );
}